/** Standard QR label dimensions (stored canonically in mm). */

export const MIN_LABEL_MM = 12;
export const MIN_LABEL_CM = 1.2;

/** @type {{ id: string, widthMm: number, heightMm: number }[]} */
export const STANDARD_QR_LABEL_SIZES = [
  { id: '12x12', widthMm: 12, heightMm: 12 },
  { id: '15x15', widthMm: 15, heightMm: 15 },
  { id: '20x20', widthMm: 20, heightMm: 20 },
  { id: '25x25', widthMm: 25, heightMm: 25 },
  { id: '30x30', widthMm: 30, heightMm: 30 },
  { id: '40x40', widthMm: 40, heightMm: 40 },
  { id: '50x50', widthMm: 50, heightMm: 50 },
  { id: '25x50', widthMm: 25, heightMm: 50 },
  { id: '100x50', widthMm: 100, heightMm: 50 },
];

export const DEFAULT_STANDARD_SIZE_ID = '25x25';
export const CUSTOM_SIZE_ID = 'custom';

export function mmToUnit(mm, unit) {
  return unit === 'cm' ? mm / 10 : mm;
}

export function unitToMm(value, unit) {
  return unit === 'cm' ? value * 10 : value;
}

export function formatSizeLabel(widthMm, heightMm, unit) {
  const w = mmToUnit(widthMm, unit);
  const h = mmToUnit(heightMm, unit);
  const u = unit;
  const fmt = (n) => (unit === 'cm' ? (Number.isInteger(n * 10) ? n.toFixed(1) : n.toFixed(2)) : String(Math.round(n)));
  return `${fmt(w)} × ${fmt(h)} ${u}`;
}

export function getStandardSizeById(id) {
  return STANDARD_QR_LABEL_SIZES.find((s) => s.id === id) || STANDARD_QR_LABEL_SIZES.find((s) => s.id === DEFAULT_STANDARD_SIZE_ID);
}

export function getStandardDimensions(sizeId, unit) {
  const spec = getStandardSizeById(sizeId);
  return {
    width: mmToUnit(spec.widthMm, unit),
    height: mmToUnit(spec.heightMm, unit),
    widthMm: spec.widthMm,
    heightMm: spec.heightMm,
  };
}

export function listStandardOptions(unit, { includeCustom = true } = {}) {
  const items = STANDARD_QR_LABEL_SIZES.map((s) => ({
    id: s.id,
    label: formatSizeLabel(s.widthMm, s.heightMm, unit),
    widthMm: s.widthMm,
    heightMm: s.heightMm,
  }));
  if (includeCustom) {
    items.push({ id: CUSTOM_SIZE_ID, label: 'Customize…', widthMm: null, heightMm: null });
  }
  return items;
}

export function minForUnit(unit) {
  return unit === 'cm' ? MIN_LABEL_CM : MIN_LABEL_MM;
}

export function validateLabelDimensions(width, height, unit) {
  const w = Number(width);
  const h = Number(height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return 'Enter valid width and height';
  }
  const wMm = unitToMm(w, unit);
  const hMm = unitToMm(h, unit);
  if (wMm < MIN_LABEL_MM || hMm < MIN_LABEL_MM) {
    return unit === 'mm'
      ? `Minimum label size is ${MIN_LABEL_MM} × ${MIN_LABEL_MM} mm`
      : `Minimum label size is ${MIN_LABEL_CM} × ${MIN_LABEL_CM} cm (${MIN_LABEL_MM} mm)`;
  }
  return null;
}
